import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  RoleDefinition,
  RoleDefinitionsService,
} from 'src/app/core/services/role-definitions.service';
import { RoleTitlesService } from 'src/app/core/services/role-titles.service';
import { RoleBuilderComponent } from 'src/app/shared/role-builder/role-builder.component';
import { BaseItemRendererComponent } from '../stored-items-browser/base-item-renderer.component';
import { NotificationService } from '../../notification-service/notification.service';
import { AuthService } from '../../../auth/service/auth.service';

// this component can be used to either:
// 1) be created dynamically inside the StoredItemsBrowserComponent (which will provide all its data via injection)
// 2) be used as a regular component from some template (which will provide its data via input properties)
@Component({
    selector: 'app-role-definition-item-renderer',
    templateUrl: './role-definition-item-renderer.component.html',
    styleUrls: ['./role-definition-item-renderer.component.scss'],
    standalone: false
})
export class RoleDefinitionItemRendererComponent
  extends BaseItemRendererComponent<RoleDefinition>
  implements OnDestroy, OnInit
{
  @Input() item: RoleDefinition;
  @Input() allowDelete: boolean = false;
  @Input() showCreator: boolean = true;

  @Output() itemSelected = new EventEmitter<RoleDefinition>();
  @Output() itemDeleted = new EventEmitter<RoleDefinition>();

  private onItemSelectedInjected: (item: RoleDefinition) => void;
  private onItemDeletedInjected: (item: RoleDefinition) => void;
  protected storageService: RoleDefinitionsService;

  isExpanded: boolean = false;
  isLoadingTitles: boolean = false;
  titles: string[] = [];
  private titlesSubscription?: Subscription;

  constructor(
    @Optional() @Inject('item') injectedItem: RoleDefinition,
    @Optional() @Inject('allowDelete') injectedAllowDelete: boolean,
    @Optional() @Inject('showCreator') injectedShowCreator: boolean,
    @Optional() @Inject('onItemSelected') onItemSelected: (item: RoleDefinition) => void,
    @Optional() @Inject('onItemDeleted') onItemDeleted: (item: RoleDefinition) => void,
    private roleTitlesService: RoleTitlesService,
    roleDefinitionsService: RoleDefinitionsService,
    dialog: MatDialog,
    notificationService: NotificationService,
    authService: AuthService
  ) {
    // Handle dual usage: dynamic component or template component
    // Read more: https://www.notion.so/livedata/Supporting-component-dual-use-dynamic-and-template-mode-25ce1d9e6b7a80dd9d1afdcf0dd883bc
    const finalItem = injectedItem || (null as any);
    const finalAllowDelete = injectedItem ? injectedAllowDelete : false;
    const finalShowCreator = injectedItem ? injectedShowCreator : true;
    const finalOnItemSelected = onItemSelected || (() => {});
    const finalOnItemDeleted = onItemDeleted || (() => {});

    super(
      finalItem,
      finalAllowDelete,
      finalShowCreator,
      finalOnItemSelected,
      finalOnItemDeleted,
      dialog,
      notificationService,
      authService
    );

    this.storageService = roleDefinitionsService;

    // Store input properties for template usage
    if (injectedItem) {
      this.allowDelete = injectedAllowDelete;
      this.showCreator = injectedShowCreator;
    }
    this.onItemSelectedInjected = onItemSelected;
    this.onItemDeletedInjected = onItemDeleted;
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.titlesSubscription?.unsubscribe();
  }

  selectItem() {
    if (this.item) {
      if (this.onItemSelectedInjected) {
        this.onItemSelectedInjected(this.item);
      } else {
        this.itemSelected.emit(this.item);
      }
    }
  }

  deleteItem(event: Event) {
    event.stopPropagation();
    if (this.item) {
      if (this.onItemDeletedInjected) {
        this.onItemDeletedInjected(this.item);
      } else {
        this.itemDeleted.emit(this.item);
      }
    }
  }

  protected getItemTypeName(): string {
    return 'Role Definition';
  }

  toggleExpansion(event: Event) {
    event.stopPropagation();
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded && this.titles.length === 0) {
      this.loadTitles();
    }
  }

  loadTitles() {
    if (!this.item || this.titles.length > 0) return;

    this.isLoadingTitles = true;
    this.titlesSubscription = this.roleTitlesService
      .getTopMatchingTitlesForRole(this.item, 15)
      .subscribe({
        next: (titles) => {
          this.titles = titles ?? [];
          this.isLoadingTitles = false;
        },
        error: () => {
          this.titles = [];
          this.isLoadingTitles = false;
        },
      });
  }

  viewDetails(event: Event) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(RoleBuilderComponent, {
      maxWidth: '1200px',
      width: '100%',
      panelClass: 'saved-lists-dialog',
      backdropClass: 'tw-bg-black/80',
      data: { role: this.item },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.deleted) {
        // Role was deleted - no need to do anything as the service handles cache updates
        return;
      } else if (result?.saveAsNew) {
        // Save as a new role
        const newRole: RoleDefinition = {
          id: '', // Will be set by the API
          name: result.name || '',
          description: result.description || '',
          criteria: result.criteria || {
            mustExact: [],
            mustFuzzy: [],
            mustNotExact: [],
            mustNotFuzzy: [],
          },
          category: result.category,
          createdAt: '', // Will be set by the API
          createdBy: '', // Will be set by the API
          scope: 'org', // Default scope
        };

        this.storageService.create(newRole).subscribe();
      } else if (result && !result.deleted) {
        // Update the existing role
        const updatedRole: RoleDefinition = {
          ...this.item,
          name: result.name || this.item.name,
          description: result.description || this.item.description,
          category: result.category !== undefined ? result.category : this.item.category,
          criteria: result.criteria || this.item.criteria,
        };

        this.storageService.update(this.item.id, updatedRole).subscribe();
      }
    });
  }
}
